const POSTHOG_CONSENT_COOKIE_NAME = "gz_posthog_consent"
const POSTHOG_CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export type PostHogConsentStatus = "granted" | "denied"

export const POSTHOG_CONSENT_CHANGE_EVENT = "posthog-consent-change"

function extractCookieValue(
  cookieString: string | null | undefined,
  cookieName: string,
): string | null {
  if (!cookieString) {
    return null
  }

  const cookies = cookieString.split(";")

  for (const rawCookie of cookies) {
    const cookie = rawCookie.trim()
    if (!cookie.startsWith(`${cookieName}=`)) {
      continue
    }

    return cookie.slice(cookieName.length + 1)
  }

  return null
}

function getSharedCookieDomain(hostname: string): string | null {
  const normalizedHostname = hostname.split(":")[0].toLowerCase()

  if (
    normalizedHostname === "grezy.org" ||
    normalizedHostname.endsWith(".grezy.org")
  ) {
    return ".grezy.org"
  }

  return null
}

export function getPostHogConsentFromCookieString(
  cookieString: string | null | undefined,
): PostHogConsentStatus | null {
  const rawValue = extractCookieValue(cookieString, POSTHOG_CONSENT_COOKIE_NAME)

  if (!rawValue) {
    return null
  }

  const decodedValue = decodeURIComponent(rawValue)

  if (decodedValue === "granted" || decodedValue === "denied") {
    return decodedValue
  }

  return null
}

export function isPostHogTrackingAllowed(
  cookieString: string | null | undefined,
): boolean {
  return getPostHogConsentFromCookieString(cookieString) !== "denied"
}

export function getPostHogConsentFromDocument(): PostHogConsentStatus | null {
  if (typeof document === "undefined") {
    return null
  }

  return getPostHogConsentFromCookieString(document.cookie)
}

export function setPostHogConsent(consent: PostHogConsentStatus): void {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return
  }

  const cookieDomain = getSharedCookieDomain(window.location.hostname)
  const isSecure = window.location.protocol === "https:"

  document.cookie = [
    `${POSTHOG_CONSENT_COOKIE_NAME}=${encodeURIComponent(consent)}`,
    `max-age=${POSTHOG_CONSENT_COOKIE_MAX_AGE}`,
    "path=/",
    "samesite=lax",
    cookieDomain ? `domain=${cookieDomain}` : null,
    isSecure ? "secure" : null,
  ]
    .filter(Boolean)
    .join("; ")

  window.dispatchEvent(
    new CustomEvent(POSTHOG_CONSENT_CHANGE_EVENT, {
      detail: consent,
    }),
  )
}
